import { Customer } from "shared";
import { ChangeEvent, useEffect, useState } from "react";

import { EditNameForm } from "./form/EditNameForm";
import {
  areCustomersTheSame,
  navigateToSettings,
  saveCustomerInfo,
} from "../../utils";
import { useScrollToTop } from "../../../../hooks/useScrollToTop";
import { useIsStateFarmPartitioned } from "../../../../app/Providers/AppContext";
import { usePolicySummaries } from "../../../../hooks/usePolicySummaries";
import { UpdateStatus, ValidationErrors } from "../../SettingsPage.types";
import { EditPageTemplate } from "../../EditPageTemplate";
import { mt } from "../../../../utils/tracking";
import { SettingsPageSkeleton } from "../../components";
import { CustomerName, CustomerNameKey } from "./EditNamePage.types";

export type EditNamePageProps = {
  initialCustomer: Customer;
};

/**
 * Edit the name fields in contact information
 */
export const EditNamePage = ({ initialCustomer }: EditNamePageProps) => {
  const isStateFarmPartitioned = useIsStateFarmPartitioned();
  const skeleton = <SettingsPageSkeleton testId="edit-name" rows={3} />;

  if (isStateFarmPartitioned) {
    navigateToSettings();
    return skeleton;
  }

  useScrollToTop();

  const policies = usePolicySummaries(true);

  // Policyholders are not allowed to edit name.
  // If policies is undefined, probably due to a network error, then we can't confirm their status,
  // so we should assume that they are a policyholder and navigate away from this page.
  if (policies?.length !== 0) {
    navigateToSettings();
    return skeleton;
  }

  return <EditNamePageWrapped initialCustomer={initialCustomer} />;
};

const EditNamePageWrapped = ({ initialCustomer }: EditNamePageProps) => {
  const [status, setStatus] = useState<UpdateStatus>("ok");
  const [customerName, setCustomerName] = useState(
    getNameFields(initialCustomer)
  );
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>(
    {}
  );
  const [changedFields, setChangedFields] = useState(
    new Set<CustomerNameKey>()
  );

  const hasValidationErrors = Object.keys(validationErrors).length > 0;

  useEffect(() => {
    mt.trackView();
    setValidationErrors(getValidationErrors(customerName));
  }, []);

  return (
    <EditPageTemplate
      heading="Change your name"
      unsavedChangesDialogHeading="Save your name changes?"
      pending={status == "pending"}
      isFormDirty={checkIfDirty()}
      hasValidationErrors={hasValidationErrors}
      showMutateError={status === "mutateError"}
      onClickSave={onClickSave}
    >
      <EditNameForm
        customer={customerName}
        changedFields={changedFields}
        validationErrors={validationErrors}
        pending={status == "pending"}
        onChange={onChange}
      />
    </EditPageTemplate>
  );

  function getNameFields({
    firstName,
    middleName,
    lastName,
  }: Customer): CustomerName {
    return { firstName, middleName, lastName };
  }

  function checkIfDirty(): boolean {
    return !areCustomersTheSame<CustomerName>(initialCustomer, customerName);
  }

  function onChange(event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) {
    const field = event.target.id as CustomerNameKey;
    const value = event.target.value;
    const updatedCustomer = { ...customerName, [field]: value };

    setCustomerName(updatedCustomer);
    setValidationErrors(getValidationErrors(updatedCustomer));
    setChangedFields(new Set(changedFields).add(field));
  }

  function getValidationErrors(customer: CustomerName): ValidationErrors {
    const errors: ValidationErrors = {};

    if (!customer.firstName.trim()) {
      errors.firstName = "Please enter your first name";
    }

    if (!customer.lastName.trim()) {
      errors.lastName = "Please enter your last name";
    }

    return errors;
  }

  async function onClickSave(): Promise<void> {
    mt.trackAction({ actionType: "button", actionName: "save" });

    setStatus("pending");

    try {
      await saveCustomerInfo({
        ...initialCustomer,
        ...trimCustomerName(customerName),
      });
      navigateToSettings(true);
    } catch {
      setStatus("mutateError");
    }
  }

  function trimCustomerName(customer: CustomerName): CustomerName {
    return {
      firstName: customer.firstName.trim(),
      middleName: customer.middleName.trim(),
      lastName: customer.lastName.trim(),
    };
  }
};
